import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, UserPlus, UserMinus, Users, Clock, MapPin, GitBranch, ImagePlus, X, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TournamentPage() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    fetchTournament();
    fetchSignups();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTournament = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/active`);
      setTournament(data);
    } catch (err) {
      console.error('tournament:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignups = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/signups`);
      setSignups(data || []);
      setIsSignedUp((data || []).some(s => s.user_id === user?.id));
    } catch (err) {
      console.error('signups:', err);
    }
  };

  const refetch = () => { fetchTournament(); fetchSignups(); };

  const handleSignup = async () => {
    try {
      await axios.post(`${API}/tournaments/signup`);
      toast.success('Signed up for the event!');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign up');
    }
  };

  const handleUnsignup = async () => {
    try {
      await axios.delete(`${API}/tournaments/signup`);
      toast.success('Sign up cancelled');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleRemoveSignup = async (signupId) => {
    try {
      await axios.delete(`${API}/admin/tournaments/signups/${signupId}`);
      toast.success('Player removed from signups');
      refetch();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !tournament) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/tournaments/${tournament.id}/image`, fd);
      toast.success('Image uploaded');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const handleImageRemove = async () => {
    if (!tournament) return;
    try {
      await axios.delete(`${API}/admin/tournaments/${tournament.id}/image`);
      toast.success('Image removed');
      refetch();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  const handleDeleteTournament = async () => {
    if (!tournament) return;
    setConfirmDelete(false);
    try {
      await axios.delete(`${API}/tournaments/${tournament.id}`);
      toast.success('Tournament deleted');
      setTournament(null);
      setSignups([]);
      setIsSignedUp(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hr12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hr12}:${m} ${ampm}`;
  };

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="tournament-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">Compete</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">
            Tournament
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tournament ? (
          <>
            {/* Active Event / Tournament */}
            <div className="bg-[#121212] border border-cyan-400/30 mb-8 overflow-hidden" data-testid="active-tournament">
              {tournament.image_url && (
                <div className="relative">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${tournament.image_url}`}
                    alt={tournament.name}
                    className="w-full max-h-72 object-cover"
                    data-testid="tournament-banner-image"
                  />
                  {isAdmin && (
                    <button
                      onClick={handleImageRemove}
                      className="absolute top-2 right-2 bg-black/80 text-red-400 hover:text-red-300 p-2 border border-red-500/30"
                      title="Remove image"
                      data-testid="remove-tournament-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              {isAdmin && !tournament.image_url && (
                <label
                  className="flex items-center justify-center gap-2 py-6 border-b border-zinc-800 bg-zinc-950 hover:bg-zinc-900 cursor-pointer transition-colors text-yellow-400 text-sm font-bold uppercase tracking-wide"
                  data-testid="upload-tournament-image-btn"
                >
                  <ImagePlus className="w-5 h-5" />
                  Upload Tournament Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              )}
              <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                  <Trophy className="w-7 h-7 text-yellow-500 mt-1 flex-shrink-0" />
                  <div>
                    <h2 className="text-white font-heading font-bold uppercase text-2xl">{tournament.name}</h2>
                    <p className="text-zinc-500 text-sm">{tournament.team_count} team bracket</p>
                  </div>
                </div>
                <div className="px-3 py-1 bg-green-500/10 border border-green-500/30 flex-shrink-0">
                  <span className="text-green-500 text-xs font-bold uppercase">{tournament.status === 'active' ? 'Active' : 'Registration Open'}</span>
                </div>
              </div>

              {tournament.description && (
                <p className="text-zinc-300 text-sm mb-4">{tournament.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-5 text-xs text-zinc-400 mb-5">
                {tournament.show_date !== false && tournament.date && (
                  <span className="flex items-center gap-2" data-testid="tournament-date">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    <span className="font-mono">{tournament.date}</span>
                    {tournament.show_time !== false && tournament.time && (
                      <span className="font-mono text-zinc-500">· {formatTime(tournament.time)}</span>
                    )}
                  </span>
                )}
                {tournament.location && (
                  <span className="flex items-center gap-2" data-testid="tournament-location">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    {tournament.location}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-zinc-800">
                {isAuthenticated ? (
                  isSignedUp ? (
                    <>
                      <span className="text-green-500 text-sm font-bold flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> You're signed up!
                      </span>
                      <Button onClick={handleUnsignup} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-none h-9" data-testid="unsignup-btn">
                        <UserMinus className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </>
                  ) : (
                    <Button onClick={handleSignup} className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-10 px-6" data-testid="signup-btn">
                      <UserPlus className="w-4 h-4 mr-2" /> Sign Up
                    </Button>
                  )
                ) : (
                  <p className="text-zinc-500 text-sm">Log in to sign up for this event.</p>
                )}
                <Link to="/bracket">
                  <Button variant="outline" className="border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 rounded-none h-10" data-testid="view-bracket-btn">
                    <GitBranch className="w-4 h-4 mr-2" /> View Bracket
                  </Button>
                </Link>
                {isAdmin && (
                  <Button
                    onClick={() => setConfirmDelete(true)}
                    variant="outline"
                    className="ml-auto border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-none h-10"
                    data-testid="delete-tournament-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Tournament
                  </Button>
                )}
              </div>
              </div>
            </div>

            {/* Signed Up Players */}
            {signups.length > 0 && (
              <div className="bg-[#121212] border border-zinc-800 p-6" data-testid="signups-list">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">Signed Up Players ({signups.length})</h2>
                </div>
                <div className="space-y-2">
                  {signups.map(s => (
                    <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-black border border-zinc-800" data-testid={`signup-${s.id}`}>
                      <div>
                        <span className="text-white text-sm font-medium">{s.user_name}</span>
                        {isAdmin && s.user_email && <span className="text-zinc-600 text-xs ml-2">{s.user_email}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-600 text-[10px]">{new Date(s.created_at).toLocaleDateString()}</span>
                        {isAdmin && (
                          <button onClick={() => handleRemoveSignup(s.id)} className="text-red-400/50 hover:text-red-400 transition-colors" data-testid={`remove-signup-${s.id}`}>
                            <UserMinus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-[#121212] border border-zinc-800" data-testid="no-tournament">
            <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Active Tournament</h2>
            <p className="text-zinc-500">Check back later for upcoming tournaments.</p>
            {isAdmin && (
              <Link to="/bracket" className="inline-block mt-6">
                <Button className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none">
                  Create Tournament
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Delete Tournament Confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-[#121212] border-red-500/30 rounded-none" data-testid="delete-tournament-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading uppercase">Delete Tournament?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Delete tournament <span className="text-white font-bold">{tournament?.name}</span> entirely? This removes the event, bracket, all signups, and the image. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800 rounded-none" data-testid="delete-tournament-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTournament}
              className="bg-red-500 text-white hover:bg-red-600 rounded-none font-bold uppercase tracking-wide"
              data-testid="delete-tournament-confirm"
            >
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
